import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import axios from '../../services/api';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { useI18n } from '../../contexts/I18nContext';
import './OAuth2RedirectHandler.scss';

const OAuth2RedirectHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setToken, setUser } = useAuth();
  const { t } = useI18n();
  const [isProcessing, setIsProcessing] = useState(true);
  const hasProcessedRef = useRef(false); // 使用ref确保跨渲染周期的持久性

  useEffect(() => {
    const handleOAuth2Callback = async () => {
      // 使用ref防止重复执行，比闭包变量更可靠
      if (hasProcessedRef.current) {
        // console.log('OAuth2 callback already processed, skipping...');
        return;
      }
      hasProcessedRef.current = true;

      const urlParams = new URLSearchParams(location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');

      // console.log('Processing OAuth2 callback with code:', code ? 'present' : 'missing');

      if (error) {
        // 处理OAuth2授权错误
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        
        let errorMessage = t('login.googleLoginFailedGeneric');
        if (error === 'access_denied') {
          errorMessage = t('login.googleLoginFailedAccessDenied') || '用户取消了Google登录授权';
        } else if (errorDescription) {
          errorMessage = `${t('login.googleLoginFailedGeneric')}: ${errorDescription}`;
        } else {
          errorMessage = `${t('login.googleLoginFailedGeneric')}: ${error}`;
        }
        
        toast.error(errorMessage, {
          duration: 5000,
          style: { maxWidth: '400px' }
        });
        navigate('/login');
        return;
      }

      if (!code) {
        // 没有授权码，登录失败
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        toast.error(t('login.googleLoginFailedNoAuthCode'));
        navigate('/login');
        return;
      }

      try {
        // 获取保存的推荐码
        const referralCode = sessionStorage.getItem('google_auth_referral_code');
        
        // 构建请求数据
        const requestData = {
          code: code
        };
        
        // 如果有推荐码，添加到请求中
        if (referralCode) {
          requestData.referralCode = referralCode;
          // 清理推荐码
          sessionStorage.removeItem('google_auth_referral_code');
        }
        
        // 发送授权码给后端，让后端处理token交换
        const loginResponse = await axios.post('/auth/google/oauth2', requestData);

        const { token, user, isReferralLogin, isNewUser } = loginResponse.data;

        // 保存认证信息
        localStorage.setItem('token', token);
        setToken(token);
        setUser(user);

        // 设置axios默认header
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // 根据isReferralLogin显示不同的提示信息
        if (isReferralLogin && !isNewUser) {
          toast.success(t('login.accountExistsNoReferralPoints'), {
            duration: 4000, 
          });
        } else {
          toast.success(t('login.accountExistsLoginSuccess'));
        }
        
        // 获取登录前保存的返回URL
        const returnUrl = sessionStorage.getItem('google_auth_return_url');
        if (returnUrl) {
          // 清理保存的返回URL
          sessionStorage.removeItem('google_auth_return_url');
          // 跳转到登录前的页面
          navigate(returnUrl);
        } else {
          // 默认跳转到首页
          navigate('/');
        }

      } catch (error) {
        console.error('Google login processing failed:', error);
        
        // 清理认证状态
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        
        let errorMessage = t('login.googleLoginFailedGeneric');
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = `${t('login.googleLoginFailedGeneric')}: ${error.message}`;
        }
        
        toast.error(errorMessage, {
          duration: 5000,
          style: { maxWidth: '400px' }
        });
        navigate('/login');
      } finally {
        setIsProcessing(false);
      }
    };

    handleOAuth2Callback();
  }, [location.search, navigate, setToken, setUser, t]);

  return (
    <LoadingSpinner 
      message={isProcessing ? t('oauth.processingGoogleLogin') || 'Processing Google login...' : t('oauth.redirecting') || 'Redirecting...'} 
    />
  );
};

export default OAuth2RedirectHandler;
